import Fuse, { type IFuseOptions } from "fuse.js";
import { Search, X } from "lucide-react";
import { useDeferredValue, useState } from "react";
import { BlogPagination } from "~/components/blog/BlogPagination";
import { PostCard } from "~/components/blog/PostCard";
import type { PostMeta } from "~/lib/mdx";

type Props = {
  allPosts: PostMeta[];
  pagePosts: PostMeta[];
  currentPage: number;
  totalPages: number;
};

const fuseOptions: IFuseOptions<PostMeta> = {
  ignoreLocation: true,
  threshold: 0.32,
  keys: [
    { name: "title", weight: 0.6 },
    { name: "excerpt", weight: 0.25 },
    { name: "category", weight: 0.1 },
    { name: "author", weight: 0.03 },
  ],
};

export function BlogPostBrowser({
  allPosts,
  pagePosts,
  currentPage,
  totalPages,
}: Props) {
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = deferredQuery.trim();
  const visiblePosts = normalizedQuery
    ? new Fuse(allPosts, fuseOptions)
        .search(normalizedQuery)
        .map((result) => result.item)
    : pagePosts;

  const hasQuery = query.trim().length > 0;

  return (
    <>
      <div className="mb-8 w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6 lg:w-1/2">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <label
            htmlFor="blog-search"
            className="text-sm font-medium text-gray-900"
          >
            Szukaj artykułu
          </label>
          <span className="text-xs text-gray-500 sm:text-sm">
            {hasQuery
              ? `Wyniki: ${visiblePosts.length} z ${allPosts.length}`
              : `Poradnik liczy ${allPosts.length} artykułów.`}
          </span>
        </div>

        <div className="relative">
          <Search
            className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            id="blog-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Np. przegląd gazowy, rozdzielnica, czujnik dymu"
            className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3 pl-11 pr-12 text-sm text-gray-900 outline-none transition focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-100"
          />
          {hasQuery && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
              aria-label="Wyczyść wyszukiwanie"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>

      </div>

      {visiblePosts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
          <p className="text-base font-medium text-gray-900">
            Nie znaleźliśmy artykułów dla frazy:{" "}
            <span className="font-semibold">{query.trim()}</span>
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Spróbuj krótszej frazy albo innego słowa kluczowego.
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visiblePosts.map((post) => (
              <PostCard key={post.slug} post={post} />
            ))}
          </div>

          {!hasQuery && (
            <BlogPagination currentPage={currentPage} totalPages={totalPages} />
          )}
        </>
      )}
    </>
  );
}
